"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { afterScroll } from "@/lib/after-interaction";

const TARGET_SELECTOR = "main img:not([data-no-image-reveal]):not([data-image-reveal-ready])";
const REVEAL_THRESHOLD = 0.15;

export function ImageRevealObserver() {
  const pathname = usePathname();

  useEffect(() => {
    if (window.matchMedia("(max-width: 767px)").matches) {
      return;
    }

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const start = () => {
      if (cancelled) return;

      const images = Array.from(
        document.querySelectorAll<HTMLImageElement>(TARGET_SELECTOR),
      ).filter((image) => !image.closest("[data-no-image-reveal-scope]"));

      if (images.length === 0) {
        return;
      }

      images.forEach((image) => {
        image.dataset.imageRevealReady = "true";
        image.classList.add("image-reveal");
        if (prefersReducedMotion) {
          image.classList.add("image-reveal--reduced");
        }
      });

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            const image = entry.target as HTMLImageElement;
            requestAnimationFrame(() => {
              image.classList.add("image-reveal-visible");
            });
            observer?.unobserve(image);
          });
        },
        { threshold: REVEAL_THRESHOLD, rootMargin: "0px 0px -6% 0px" },
      );

      images.forEach((image) => {
        observer?.observe(image);
      });
    };

    const detach = afterScroll(() => {
      requestAnimationFrame(() => requestAnimationFrame(start));
    }, 15000);

    return () => {
      cancelled = true;
      detach();
      observer?.disconnect();
    };
  }, [pathname]);

  return null;
}
